import { of } from 'rxjs'

// ── 3.1 Company Profile ─────────────────────────────────────────────
const COMPANY_PROFILE = {
  companyId: 42,
  name: 'Acme Corporation',
  domain: 'acme.com',
  description:
    'Acme Corporation is a leading provider of enterprise cloud security solutions, specializing in email protection and threat intelligence.',
  industry: 'Computer & Network Security',
  companyType: 'Privately Held',
  foundedYear: 2018,
  location: {
    country: 'United States',
    state: 'California',
    city: 'San Francisco',
  },
  headcount: 250,
  headcountRange: '201-500',
  sources: {
    linkedin: 4,
    facebook: 2,
    twitter: 5,
    news: 12,
    web: 3,
    github: 1,
  },
  totalSources: 27,
  defenseCoveragePercent: 73.5,
  osintCreditsLeft: 16,
  nextScheduledRunDate: '2026-03-16T12:30:34.242176Z',
  lastOsintScan: '2026-03-09T12:30:34.242176Z',
  nextOsintSchedule: '2026-03-16T12:30:34.242176Z',
  isActiveAgent: true,
}

// ── 3.2 Attack Progress Analytics ───────────────────────────────────
const ATTACK_PROGRESS = {
  interval: '1w',
  timePeriod: {
    startDate: '2025-12-01T00:00:00Z',
    endDate: '2026-02-22T23:59:59Z',
    totalPeriods: 12,
  },
  periods: [
    {
      periodStart: '2025-12-01T00:00:00Z',
      periodEnd: '2025-12-07T23:59:59Z',
      periodLabel: 'Dec 1 - Dec 7',
      potentialAttacks: 12,
      learnedAttacks: 6,
    },
    {
      periodStart: '2025-12-08T00:00:00Z',
      periodEnd: '2025-12-14T23:59:59Z',
      periodLabel: 'Dec 8 - Dec 14',
      potentialAttacks: 18,
      learnedAttacks: 3,
    },
    {
      periodStart: '2025-12-15T00:00:00Z',
      periodEnd: '2025-12-21T23:59:59Z',
      periodLabel: 'Dec 15 - Dec 21',
      potentialAttacks: 25,
      learnedAttacks: 6,
    },
    {
      periodStart: '2025-12-22T00:00:00Z',
      periodEnd: '2025-12-28T23:59:59Z',
      periodLabel: 'Dec 22 - Dec 28',
      potentialAttacks: 10,
      learnedAttacks: 8,
    },
    {
      periodStart: '2025-12-29T00:00:00Z',
      periodEnd: '2026-01-04T23:59:59Z',
      periodLabel: 'Dec 29 - Jan 4',
      potentialAttacks: 15,
      learnedAttacks: 13,
    },
    {
      periodStart: '2026-01-05T00:00:00Z',
      periodEnd: '2026-01-11T23:59:59Z',
      periodLabel: 'Jan 5 - Jan 11',
      potentialAttacks: 13,
      learnedAttacks: 1,
    },
    {
      periodStart: '2026-01-12T00:00:00Z',
      periodEnd: '2026-01-18T23:59:59Z',
      periodLabel: 'Jan 12 - Jan 18',
      potentialAttacks: 13,
      learnedAttacks: 13,
    },
    {
      periodStart: '2026-01-19T00:00:00Z',
      periodEnd: '2026-01-25T23:59:59Z',
      periodLabel: 'Jan 19 - Jan 25',
      potentialAttacks: 0,
      learnedAttacks: 0,
    },
    {
      periodStart: '2026-01-26T00:00:00Z',
      periodEnd: '2026-02-01T23:59:59Z',
      periodLabel: 'Jan 26 - Feb 1',
      potentialAttacks: 8,
      learnedAttacks: 6,
    },
    {
      periodStart: '2026-02-02T00:00:00Z',
      periodEnd: '2026-02-08T23:59:59Z',
      periodLabel: 'Feb 2 - Feb 8',
      potentialAttacks: 1,
      learnedAttacks: 6,
    },
    {
      periodStart: '2026-02-09T00:00:00Z',
      periodEnd: '2026-02-15T23:59:59Z',
      periodLabel: 'Feb 9 - Feb 15',
      potentialAttacks: 9,
      learnedAttacks: 8,
    },
    {
      periodStart: '2026-02-16T00:00:00Z',
      periodEnd: '2026-02-22T23:59:59Z',
      periodLabel: 'Feb 16 - Feb 22',
      potentialAttacks: 4,
      learnedAttacks: 3,
    },
  ],
}

// ── 3.5 Company Risk ───────────────────────────────────────────────
const COMPANY_RISK = {
  companyId: 42,
  windowDays: 60,
  score: { badge: 'High', high: 163 },
  surface: { total: 1011, high: 163, medium: 498, low: 350 },
  closure: {
    closurePercent: 76,
    breakdown: {
      high: { adopted: 18, total: 30 },
      medium: { adopted: 42, total: 60 },
      low: { adopted: 2, total: 14 },
    },
  },
}

// ── 3.6 Risk Trend (13 weeks, Figma-aligned curves) ────────────────
const buildRiskTrendWeeks = () => {
  // stories.* = event counts; riskSurface.discovered = intra-week peak;
  // riskSurface.adapted = closing running balance
  const discoveredValues = [42, 38, 35, 30, 25, 20, 18, 15, 12, 10, 8, 6, 4]
  const adaptedValues = [5, 10, 15, 20, 25, 30, 28, 25, 20, 18, 15, 12, 10]

  const weekStarts = [
    '2026-04-01T00:00:00Z',
    '2026-04-08T00:00:00Z',
    '2026-04-15T00:00:00Z',
    '2026-04-22T00:00:00Z',
    '2026-04-29T00:00:00Z',
    '2026-05-06T00:00:00Z',
    '2026-05-13T00:00:00Z',
    '2026-05-20T00:00:00Z',
    '2026-05-27T00:00:00Z',
    '2026-06-03T00:00:00Z',
    '2026-06-10T00:00:00Z',
    '2026-06-17T00:00:00Z',
    '2026-06-24T00:00:00Z',
  ]
  const weekEnds = [
    '2026-04-07T23:59:59Z',
    '2026-04-14T23:59:59Z',
    '2026-04-21T23:59:59Z',
    '2026-04-28T23:59:59Z',
    '2026-05-05T23:59:59Z',
    '2026-05-12T23:59:59Z',
    '2026-05-19T23:59:59Z',
    '2026-05-26T23:59:59Z',
    '2026-06-02T23:59:59Z',
    '2026-06-09T23:59:59Z',
    '2026-06-16T23:59:59Z',
    '2026-06-23T23:59:59Z',
    '2026-06-30T23:59:59Z',
  ]
  const labels = [
    'Apr 1 - Apr 7',
    'Apr 8 - Apr 14',
    'Apr 15 - Apr 21',
    'Apr 22 - Apr 28',
    'Apr 29 - May 5',
    'May 6 - May 12',
    'May 13 - May 19',
    'May 20 - May 26',
    'May 27 - Jun 2',
    'Jun 3 - Jun 9',
    'Jun 10 - Jun 16',
    'Jun 17 - Jun 23',
    'Jun 24 - Jun 30',
  ]

  let balance = 0
  return weekStarts.map((start, index) => {
    const discovered = discoveredValues[index]
    const adapted = adaptedValues[index]
    const peak = balance + discovered
    balance = Math.max(0, peak - adapted)
    return {
      week: {
        start,
        end: weekEnds[index],
        label: labels[index],
      },
      stories: { discovered, adapted },
      riskSurface: { discovered: peak, adapted: balance },
    }
  })
}

const RISK_TREND = {
  companyId: 42,
  windowDays: 91,
  timePeriod: {
    startDate: '2026-04-01T00:00:00Z',
    endDate: '2026-06-30T23:59:59Z',
    totalPeriods: 13,
  },
  weeks: buildRiskTrendWeeks(),
}

// ── 3.4 Stories ─────────────────────────────────────────────────────
const STORIES_TEMPLATE = [
  {
    id: 101,
    sourceItemId: 98765,
    title: 'Series B Funding of $50M Led by Sequoia Capital',
    type: 'funding_rounds',
    summary:
      'Acme Corporation announced $50M Series B funding led by Sequoia Capital to expand enterprise sales team and accelerate product development.',
    createdAt: '2026-01-12T14:30:00Z',
    expiresAt: '2026-02-12T23:59:00Z',
    sources: [
      {
        url: 'https://techcrunch.com/2026/01/acme-series-b',
        kind: 'news',
        sourceName: 'TechCrunch',
        date: '2026-01-28T09:00:00Z',
      },
    ],
    attackEmail: {
      id: 201,
      subject: 'Re: Series B Announcement - Confidential Partnership Opportunity',
      sender: 'David Chen <david.chen@sequoia-ventures.net>',
      body: "Hi,\n\nCongratulations on the Series B close! I'm reaching out from one of our portfolio companies that's been looking to partner with a cybersecurity leader.\n\nGiven your expansion plans, I think there's a strong synergy. Could we set up a quick 15-min call this week to discuss?\n\nI've attached a brief overview of the partnership terms for your review.\n\nBest regards,\nDavid Chen\nPartner, Sequoia Ventures",
      trainingNote:
        "This attack exploits the publicly announced funding round to establish credibility. Red flags: (1) Lookalike domain 'sequoia-ventures.net' instead of official Sequoia domain, (2) Unsolicited attachment with 'partnership terms', (3) Urgency to schedule a call quickly, (4) Vague reference to 'portfolio companies' without specifics.",
      targetDepartment: 'Executive',
    },
  },
  {
    id: 102,
    sourceItemId: 98432,
    title: 'Acme Corp Partners with BigCorp for Enterprise Security',
    type: 'partnerships',
    summary:
      'Partnership to deliver integrated email security and threat intelligence solutions for enterprise customers.',
    createdAt: '2026-01-10T08:00:00Z',
    expiresAt: '2026-02-28T00:00:00Z',
    sources: [
      {
        url: 'https://techcrunch.com/2026/01/acme-bigcorp-partnership',
        kind: 'news',
        sourceName: 'TechCrunch',
        date: '2026-01-09T09:00:00Z',
      },
      {
        url: 'https://linkedin.com/posts/acme-partnership-announcement',
        kind: 'linkedin',
        sourceName: 'LinkedIn',
        date: '2026-01-09T15:30:00Z',
      },
    ],
    attackEmail: null,
  },
  {
    id: 103,
    sourceItemId: 98210,
    title: 'VP of Engineering Departure Announced on LinkedIn',
    type: 'executive_changes',
    summary:
      'Key leadership change detected: VP of Engineering posted farewell message on LinkedIn, signaling organizational restructuring.',
    createdAt: '2026-01-08T11:20:00Z',
    expiresAt: '2026-02-08T23:59:00Z',
    sources: [
      {
        url: 'https://linkedin.com/posts/vp-departure',
        kind: 'linkedin',
        sourceName: 'LinkedIn',
        date: '2026-01-07T16:00:00Z',
      },
    ],
    attackEmail: {
      id: 202,
      subject: 'Urgent: Engineering Team Transition - Action Required',
      sender: 'Sarah Mitchell <s.mitchell@acme-corp.io>',
      body: "Hi Team,\n\nAs you may have heard, we're going through some leadership changes in Engineering. To ensure a smooth transition, I need everyone to update their access credentials through our new portal.\n\nPlease click the link below and re-authenticate within 24 hours:\n[Secure Portal Link]\n\nThis is mandatory for all engineering staff.\n\nBest,\nSarah Mitchell\nInterim VP of Engineering",
      trainingNote:
        "This attack leverages the publicly known executive departure to create urgency. Red flags: (1) Lookalike domain 'acme-corp.io' instead of real company domain, (2) Urgency with 24-hour deadline, (3) Request to re-authenticate credentials, (4) Impersonating a new authority figure.",
      targetDepartment: 'Engineering',
    },
  },
  {
    id: 104,
    sourceItemId: 97988,
    title: 'Acme Corporation Expands to European Market',
    type: 'expansion',
    summary:
      'Company opening new offices in London and Berlin as part of international expansion strategy announced in press release.',
    createdAt: '2026-01-05T09:00:00Z',
    expiresAt: '2026-02-05T23:59:00Z',
    sources: [
      {
        url: 'https://reuters.com/acme-europe-expansion',
        kind: 'news',
        sourceName: 'Reuters',
        date: '2026-01-04T14:00:00Z',
      },
    ],
    attackEmail: null,
  },
  {
    id: 105,
    sourceItemId: 97645,
    title: 'New Product Launch: AI-Powered Threat Detection Platform',
    type: 'product_launches',
    summary:
      'Acme launched a new AI-driven threat detection platform, covered extensively in cybersecurity media outlets.',
    createdAt: '2026-01-02T16:45:00Z',
    expiresAt: '2026-02-02T23:59:00Z',
    sources: [
      {
        url: 'https://darkreading.com/acme-ai-launch',
        kind: 'news',
        sourceName: 'Dark Reading',
        date: '2026-01-02T10:00:00Z',
      },
      {
        url: 'https://twitter.com/acme/status/12345',
        kind: 'twitter',
        sourceName: 'Twitter',
        date: '2026-01-02T11:30:00Z',
      },
    ],
    attackEmail: {
      id: 203,
      subject: 'Beta Access: AI Threat Detection Platform - Exclusive Preview',
      sender: 'Acme Product Team <product@acme-security.com>',
      body: "Hello,\n\nWe're excited to offer you exclusive early access to our new AI-Powered Threat Detection Platform.\n\nAs a valued customer, you've been selected for our private beta program. Please download the installer from the link below to get started:\n\n[Download Beta Installer]\n\nThis offer expires in 48 hours.\n\nBest regards,\nAcme Product Team",
      trainingNote:
        "This attack uses the real product launch to make a fake beta invitation seem credible. Red flags: (1) Domain 'acme-security.com' is not the official domain, (2) Request to download and install software, (3) Artificial scarcity with 48-hour expiration, (4) Generic greeting instead of personalized.",
      targetDepartment: 'All',
    },
  },
  {
    id: 106,
    sourceItemId: 97321,
    title: 'Acme Corp Featured in "Top 50 Cybersecurity Companies" List',
    type: 'partnerships',
    summary:
      'Industry recognition placing the company among top cybersecurity firms, widely shared across social media.',
    createdAt: '2025-12-28T12:00:00Z',
    expiresAt: '2026-01-28T23:59:00Z',
    sources: [
      {
        url: 'https://cybersecuritynews.com/top-50-2025',
        kind: 'news',
        sourceName: 'Cybersecurity News',
        date: '2025-12-27T09:00:00Z',
      },
    ],
    attackEmail: null,
  },
  {
    id: 107,
    sourceItemId: 97102,
    title: 'Data Breach at Competitor Highlights Industry Vulnerability',
    type: 'data_breaches',
    summary:
      'Major competitor suffered a data breach affecting 2M users, raising questions about industry-wide security practices.',
    createdAt: '2025-12-22T08:30:00Z',
    expiresAt: '2026-01-22T23:59:00Z',
    sources: [
      {
        url: 'https://techcrunch.com/competitor-breach',
        kind: 'news',
        sourceName: 'TechCrunch',
        date: '2025-12-21T15:00:00Z',
      },
      {
        url: 'https://facebook.com/posts/competitor-response',
        kind: 'facebook',
        sourceName: 'Facebook',
        date: '2025-12-22T10:00:00Z',
      },
    ],
    attackEmail: {
      id: 204,
      subject: 'URGENT: Security Assessment Required - Industry Breach Alert',
      sender: 'IT Security <security@acme-internal.net>',
      body: 'All Staff,\n\nDue to the recent data breach at [Competitor], our security team is conducting an emergency assessment of all employee accounts.\n\nPlease verify your credentials immediately by logging into our security portal:\n[Security Portal]\n\nAll employees must complete this verification by end of business today.\n\nIT Security Department',
      trainingNote:
        "This attack leverages a real industry breach to create fear and urgency. Red flags: (1) Fake internal domain 'acme-internal.net', (2) End-of-day urgency, (3) Request to verify credentials via external link, (4) Impersonating IT Security with no specific contact person.",
      targetDepartment: 'All',
    },
  },
  {
    id: 108,
    sourceItemId: 96888,
    title: 'Company Layoffs in Marketing Department',
    type: 'layoffs',
    summary:
      'Reports of workforce reduction in the marketing department affecting approximately 15 positions.',
    createdAt: '2025-12-18T14:00:00Z',
    expiresAt: '2026-01-18T23:59:00Z',
    sources: [
      {
        url: 'https://linkedin.com/posts/layoff-discussion',
        kind: 'linkedin',
        sourceName: 'LinkedIn',
        date: '2025-12-17T20:00:00Z',
      },
    ],
    attackEmail: null,
  },
  {
    id: 109,
    sourceItemId: 96645,
    title: 'Acme Corp Announces Merger Talks with SecureNet Inc',
    type: 'mergers_acquisitions',
    summary:
      'Preliminary merger discussions reported between Acme Corporation and SecureNet Inc, a cloud security startup.',
    createdAt: '2025-12-15T10:15:00Z',
    expiresAt: '2026-01-15T23:59:00Z',
    sources: [
      {
        url: 'https://bloomberg.com/acme-securenet-merger',
        kind: 'news',
        sourceName: 'Bloomberg',
        date: '2025-12-14T08:00:00Z',
      },
      {
        url: 'https://linkedin.com/posts/merger-rumor',
        kind: 'linkedin',
        sourceName: 'LinkedIn',
        date: '2025-12-15T09:00:00Z',
      },
    ],
    attackEmail: {
      id: 205,
      subject: 'Confidential: Merger Integration - Document Review Required',
      sender: 'Legal Dept <legal@acme-securenet.com>',
      body: 'Dear Team,\n\nAs part of the upcoming merger integration process, we need all department heads to review and sign the attached confidential documents.\n\nPlease download the secure document package and return signed copies within 72 hours.\n\n[Download Document Package]\n\nNote: This information is strictly confidential. Do not forward this email.\n\nRegards,\nMerger Integration Team',
      trainingNote:
        "This attack exploits publicly reported merger talks. Red flags: (1) Fake merged-entity domain 'acme-securenet.com', (2) Request to download 'secure document package', (3) Confidentiality warning designed to prevent victims from verifying, (4) No specific sender name.",
      targetDepartment: 'Executive',
    },
  },
  {
    id: 110,
    sourceItemId: 96412,
    title: 'New Regulatory Compliance Requirements for Cybersecurity Firms',
    type: 'legal_regulatory',
    summary:
      'New SEC cybersecurity disclosure rules affecting publicly traded companies and their vendors, including mandatory incident reporting.',
    createdAt: '2025-12-10T07:30:00Z',
    expiresAt: '2026-01-10T23:59:00Z',
    sources: [
      {
        url: 'https://sec.gov/cybersecurity-rules-2025',
        kind: 'web',
        sourceName: 'SEC.gov',
        date: '2025-12-09T12:00:00Z',
      },
    ],
    attackEmail: null,
  },
  {
    id: 111,
    sourceItemId: 96413,
    title: 'Leaked Credentials Found in Public Repository',
    type: 'data_breaches',
    summary:
      'Developer accidentally committed internal API keys to a public GitHub repository, exposing internal services.',
    createdAt: '2025-12-05T09:30:00Z',
    expiresAt: '2026-01-05T23:59:00Z',
    sources: [
      {
        url: 'https://github.com/acme-corp/frontend',
        kind: 'github',
        sourceName: 'GitHub',
        date: '2025-12-04T12:00:00Z',
      },
    ],
    attackEmail: null,
  },
]

const STORIES_TOTAL = 47

const ALL_STORIES = Array.from({ length: STORIES_TOTAL }, (_, index) => {
  const template = STORIES_TEMPLATE[index % STORIES_TEMPLATE.length]
  return {
    ...template,
    id: index + 1,
    sourceItemId: template.sourceItemId + index,
  }
})

const paginateStories = (page = 1, pageSize = 10) => {
  const safePage = Math.max(1, page)
  const safePageSize = Math.max(1, Math.min(100, pageSize))
  const start = (safePage - 1) * safePageSize
  const stories = ALL_STORIES.slice(start, start + safePageSize)
  return {
    stories,
    total: STORIES_TOTAL,
    page: safePage,
    pageSize: safePageSize,
    pages: Math.ceil(STORIES_TOTAL / safePageSize),
  }
}

// ── Mock API ────────────────────────────────────────────────────────
const mockRedTeamAPI = {
  getRedTeamStatus(companyId = '') {
    const response = structuredClone(COMPANY_PROFILE)
    if (companyId) response.companyId = companyId
    return of(response)
  },

  getAttackGenerationData(companyId = '') {
    const response = structuredClone(ATTACK_PROGRESS)
    if (companyId) response.companyId = companyId
    return of(response)
  },

  getCompanyRisk(companyId = '') {
    const response = structuredClone(COMPANY_RISK)
    if (companyId) response.companyId = companyId
    return of(response)
  },

  getCompanyRiskTrend(companyId = '') {
    const response = structuredClone(RISK_TREND)
    if (companyId) response.companyId = companyId
    return of(response)
  },

  getTrainingActivity(companyId = '', page = 0, perPage = 10) {
    const response = structuredClone(paginateStories(page + 1, perPage))
    if (companyId) response.companyId = companyId
    return of(response)
  },

  submitAttackFeedback(storyId, attackEmailId, feedback) {
    return of({ success: true, feedback })
  },
}

export default mockRedTeamAPI
